import {Directive, HostListener, EventEmitter, Renderer2, OnInit, OnDestroy, ElementRef} from '@angular/core';
import {AppScope} from '../services/app_scope.service';
import * as utils from '../lib/utils';
import {takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";

@Directive({selector: '[appScrollable]'})
export class ScrollableDirective {
    constructor(private _el: ElementRef) {
    }

    set scrollTop(value: number) {
        this._el.nativeElement.scrollTop = value;
    }
}

@Directive({
    selector: '[appMention]',
    host: {
        '(document:click)': 'windowClick($event)', //This gets destroyed when component using this is destroyed
    },
})
export class MentionDirective implements OnDestroy {
    searchString: string = '';
    text: string = '';
    track: boolean = false;
    //https://github.com/component/textarea-caret-position/blob/master/LICENSE
    properties = [
        'direction',  // RTL support
        'boxSizing',
        'width',  // on Chrome and IE, exclude the scrollbar, so the mirror div wraps exactly as the textarea does
        'height',
        'overflowX',
        'overflowY',  // copy the scrollbar for IE

        'borderTopWidth',
        'borderRightWidth',
        'borderBottomWidth',
        'borderLeftWidth',
        'borderStyle',

        'paddingTop',
        'paddingRight',
        'paddingBottom',
        'paddingLeft',

        // https://developer.mozilla.org/en-US/docs/Web/CSS/font
        'fontStyle',
        'fontVariant',
        'fontWeight',
        'fontStretch',
        'fontSize',
        'fontSizeAdjust',
        'lineHeight',
        'fontFamily',

        'textAlign',
        'textTransform',
        'textIndent',
        'textDecoration',  // might not make a difference, but better be safe

        'letterSpacing',
        'wordSpacing',

        'tabSize',
        'MozTabSize'

    ];
    private readonly onDestroy = new Subject<void>();

    constructor(private appScope: AppScope,
                private renderer: Renderer2) {
        this.appScope.at_who = false;

        this.appScope.mentionSelected.pipe(takeUntil(this.onDestroy)).subscribe(value => {
            this.reset();
        })
    }

    windowClick(event) {
        if (this.appScope.at_who === true && this.track === true && event.target.id !== 'at_who_user_list') {
            this.reset();
        }
    }

    reset() {
        //Reset all variables used to track events and search after @ detected
        //Also emits searchString("") to reset at_who component
        this.searchString = '';
        this.appScope.mentionEmit.emit(this.searchString);
        this.appScope.at_who_index = null;
        this.text = '';
        this.renderer.setStyle(document.getElementById('at_who'), 'display', 'none');
        this.appScope.at_who = false;
        this.track = false;
    }

    @HostListener('keydown', ['$event']) newMention(event) {
        if (event.key === "@") {
            this.searchString = '';
            this.text = utils.deepCopy(event.target.value); //What the text is when @ is first pressed
            this.appScope.at_who_index = utils.deepCopy(event.target.selectionStart); //Where the @ occurred

            let sub_start = utils.deepCopy(this.text).substr(this.appScope.at_who_index - 1, 1);
            let starts_with = sub_start.startsWith(" ") === true ? true : sub_start.startsWith('\n');

            let sub_ends = utils.deepCopy(this.text).substr(this.appScope.at_who_index, 1);
            let ends_with = sub_ends.endsWith(" ") === true ? true : sub_ends.endsWith('\n');

            //Check for spaces before and after @
            if ((ends_with || this.appScope.at_who_index === event.target.selectionEnd) &&
                (starts_with || this.appScope.at_who_index === 0)) {
                this.getPosition(event, utils.deepCopy(this.text));

                //Check using @ViewChild in appComponent and setting it in appScope
                this.renderer.setStyle(document.getElementById('at_who'), 'display', 'block');
                this.renderer.setStyle(document.getElementById('at_who'), 'left', `${this.appScope.at_who_coords.left}px`);
                this.renderer.setStyle(document.getElementById('at_who'), 'top', `${this.appScope.at_who_coords.top}px`);

                this.appScope.at_who = true;
            } else {
                this.text = "";
                this.appScope.at_who_index = 0;
                return;
            }
        }
        if (this.appScope.at_who === true) {
            if (event.key === "Tab" || event.key === "Enter") {
                event.preventDefault();
                this.appScope.mentionEmit.emit("select");
                this.reset();
            }
            if (event.key === "ArrowUp" || event.key === "ArrowDown") {
                event.preventDefault();
                this.appScope.mentionEmit.emit(event.key)
            }
            if (event.key === "Escape") {
                console.log('Escape');
                this.reset();
            }
        }
    }

    @HostListener('keyup', ['$event']) upMention(event) {
        //Key up gets the value of the typed in string *including* this event key
        if (this.appScope.at_who === true && this.track === true) {
            this.searchString = event.target.value.substring(this.appScope.at_who_index, event.target.selectionStart);

            if (this.searchString === "@ ") {
                this.reset();
                return;
            }
            if (event.key === "Backspace" && this.searchString === '') {
                this.reset();
                return;
            }
            if (event.key === "ArrowUp" || event.key === "ArrowDown") {
                event.preventDefault();
                return;
            }
            this.appScope.mentionEmit.emit(this.searchString);
        }
        if (this.appScope.at_who === true) {
            this.track = true;
        } else {
            this.track = false;
        }
    }

    ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.unsubscribe();
    }

    getPosition(event, text) {
        let coords = this.getCaretCoordinates(event.target, event.target.selectionStart);
        let top = event.target.getBoundingClientRect().top + coords.top + coords.height - event.target.scrollTop;
        let left = event.target.getBoundingClientRect().left + coords.left;

        this.appScope.at_who_coords.top = top;
        this.appScope.at_who_coords.left = left - 2;
    }

    getCaretCoordinates(element, position, options?) {
        //console.log("element scroll", element.scrollTop)
        var debug = options && options.debug || false;
        if (debug) {
            var el = document.querySelector('#input-textarea-caret-position-mirror-div');
            if (el) el.parentNode.removeChild(el);
        }

        // The mirror div will replicate the textarea's style
        let div = document.createElement('div');
        div.id = 'input-textarea-caret-position-mirror-div';
        document.body.appendChild(div);

        let style = div.style;
        let computed = window.getComputedStyle ? window.getComputedStyle(element) : element.currentStyle;  // currentStyle for IE < 9
        let isInput = element.nodeName === 'INPUT';

        // Default textarea styles
        style.whiteSpace = 'pre-wrap';
        if (!isInput)
            style.wordWrap = 'break-word';  // only for textarea-s

        // Position off-screen
        style.position = 'absolute';  // required to return coordinates properly
        if (!debug)
            style.visibility = 'hidden';  // not 'display: none' because we want rendering

        // Transfer the element's properties to the div
        this.properties.forEach(function (prop) {
            if (isInput && prop === 'lineHeight') {
                // Special case for <input>s because text is rendered centered and line height may be != height
                if (computed.boxSizing === "border-box") {
                    var height = parseInt(computed.height);
                    var outerHeight =
                        parseInt(computed.paddingTop) +
                        parseInt(computed.paddingBottom) +
                        parseInt(computed.borderTopWidth) +
                        parseInt(computed.borderBottomWidth);
                    var targetHeight = outerHeight + parseInt(computed.lineHeight);
                    if (height > targetHeight) {
                        style.lineHeight = height - outerHeight + "px";
                    } else if (height === targetHeight) {
                        style.lineHeight = computed.lineHeight;
                    } else {
                        style.lineHeight = '0';
                    }
                } else {
                    style.lineHeight = computed.height;
                }
            } else {
                style[prop] = computed[prop];
            }
        });


        style.overflow = 'hidden';  // for Chrome to not render a scrollbar; IE keeps overflowY = 'scroll'


        div.textContent = element.value.substring(0, position);
        // The second special handling for input type="text" vs textarea:
        // spaces need to be replaced with non-breaking spaces - http://stackoverflow.com/a/13402035/1269037
        if (isInput)
            div.textContent = div.textContent.replace(/\s/g, '\u00a0');

        var span = document.createElement('span');
        // Wrapping must be replicated *exactly*, including when a long word gets
        // onto the next line, with whitespace at the end of the line before (#7).
        // The  *only* reliable way to do that is to copy the *entire* rest of the
        // textarea's content into the <span> created at the caret position.
        // For inputs, just '.' would be enough, but no need to bother.
        span.textContent = element.value.substring(position) || '.';  // || because a completely empty faux span doesn't render at all
        div.appendChild(span);

        var coordinates = {
            top: span.offsetTop + parseInt(computed['borderTopWidth']),
            left: span.offsetLeft + parseInt(computed['borderLeftWidth']),
            height: parseInt(computed['lineHeight'])
        };

        if (debug) {
            span.style.backgroundColor = '#aaa';
        } else {
            document.body.removeChild(div);
        }

        return coordinates;
    }

    //The MIT License (MIT)
    //
    // Copyright (c) 2015 Jonathan Ong me@jongleberry.com
    //
    // Permission is hereby granted, free of charge, to any person obtaining a copy of this software
    // and associated documentation files (the "Software"), to deal in the Software without restriction,
    // including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense,
    // and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so,
    // subject to the following conditions:
    //
    // The above copyright notice and this permission notice shall be included in all copies or substantial
    // portions of the Software.
    //
    // THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT
    // LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
    // IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY,
    // WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
    // SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
}


